import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";

/**
 * Where the persisted query cache lives: IndexedDB in the browser (no 5 MB
 * localStorage cap, and reads do not block the main thread), AsyncStorage on
 * native. Values are JSON strings. Every key this module writes starts with
 * CACHE_KEY_PREFIX, which is what `removeAll` wipes.
 */
export type CacheStorage = {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  /** Removes every persisted query cache (all users, all orgs). */
  removeAll(): Promise<void>;
};

export const CACHE_KEY_PREFIX = "sc-query-cache:";

const DB_NAME = "selise-club-cache";
const STORE = "query-cache";

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

let dbPromise: Promise<IDBDatabase> | null = null;
const db = () => {
  dbPromise ??= openDb().catch((error) => {
    dbPromise = null;
    throw error;
  });
  return dbPromise;
};

const run = async <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const database = await db();
  return new Promise<T>((resolve, reject) => {
    const tx = database.transaction(STORE, mode);
    const request = action(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(request.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

const indexedDbStorage: CacheStorage = {
  get: async (key) => {
    const value = await run("readonly", (s) => s.get(key));
    return typeof value === "string" ? value : null;
  },
  set: async (key, value) => {
    await run("readwrite", (s) => s.put(value, key));
  },
  remove: async (key) => {
    await run("readwrite", (s) => s.delete(key));
  },
  removeAll: async () => {
    await run("readwrite", (s) => s.clear());
  },
};

const asyncStorage: CacheStorage = {
  get: (key) => AsyncStorage.getItem(key),
  set: (key, value) => AsyncStorage.setItem(key, value),
  remove: (key) => AsyncStorage.removeItem(key),
  removeAll: async () => {
    const keys = await AsyncStorage.getAllKeys();
    const ours = keys.filter((k) => k.startsWith(CACHE_KEY_PREFIX));
    if (ours.length) await AsyncStorage.multiRemove(ours);
  },
};

const noStorage: CacheStorage = {
  get: async () => null,
  set: async () => {},
  remove: async () => {},
  removeAll: async () => {},
};

export const defaultCacheStorage = (): CacheStorage => {
  if (Platform.OS !== "web") return asyncStorage;
  // Private windows and some embedded browsers have no IndexedDB; the app then
  // simply runs without a persisted cache.
  return typeof indexedDB === "undefined" ? noStorage : indexedDbStorage;
};
